/**
 * The chart: land, rings and spawn lines.
 *
 * A layout is a handful of island seeds — each an ellipse on the sea with a
 * height, which `terrain.ts` turns into a height field with a real coast —
 * plus where the capture rings are and where the two teams form up. The match
 * seed picks the layout the way it picks the weather, so every client and the
 * server agree on the shore without a byte of it on the wire.
 *
 * What land does, all of it here so the server and the predicting client
 * agree to the metre:
 * - a hull that runs onto it is stopped and pushed back to the water
 *   (`groundHull`), the way the arena wall stops one;
 * - a shell that comes down on it lands on rock, not water (`onLand`);
 * - a fish that runs into it is spent (`onLand` again);
 * - a hull behind it cannot be seen from the far side (`losBlocked`), which
 *   is the whole point of an island: somewhere to be that the enemy is not
 *   looking at.
 *
 * The rings belong to the layout because where they are is what makes one
 * chart play differently from another: three in a row across open water is a
 * points race along one line; three along a fjord is a fight for a channel;
 * one in a lagoon and two outside the reef is a choice between the sheltered
 * ring and the open ones. `objectives.ts` owns the rules for taking a ring
 * and the ring size; this file owns where they stand.
 *
 * This file imports only types and `terrain.ts` on purpose. `simulation.ts`
 * and `protocol.ts` import it, so the numbers below are literals;
 * `islands.test` checks them against `ARENA_HALF`, `LINE_DISTANCE` and
 * `ZONE_RADIUS` rather than deriving them here and closing an import cycle.
 *
 * The client draws the land from the same field (`engine/islands.ts`).
 */
import type { ZoneLayout } from './objectives';
import type { Team } from './protocol';
import { EYE_HEIGHT, LOS_STEP, TERRAIN_CELL, terrainFor } from './terrain';

/**
 * An island seed. The coast the field draws wanders up to a quarter of the
 * longer axis from this ellipse either way (`terrain.ts`), so a seed is where
 * an island roughly is, not its outline.
 */
export interface Island {
  x: number;
  z: number;
  /** Half-axes of the seed ellipse, metres. */
  rx: number;
  rz: number;
  /** Rotation of the ellipse, radians, clockwise from +Z like a heading. */
  yaw: number;
  /** Peak height above the sea, metres, before the field's relief. */
  height: number;
}

/**
 * Where a team forms up: the middle of its line and the way it faces. Slots
 * spread out to either side, perpendicular to the facing (`spawnSlot`).
 */
export interface SpawnLine {
  x: number;
  z: number;
  /** Heading, radians clockwise from +Z, like a ship's yaw. */
  yaw: number;
}

export type LayoutId = 'open' | 'archipelago' | 'strait' | 'atoll' | 'fjord' | 'twins' | 'shoals' | 'headland' | 'delta' | 'crescent' | 'sound';

export interface IslandLayout {
  id: LayoutId;
  islands: readonly Island[];
  /** The capture rings, in the order they go on the wire. */
  zones: readonly ZoneLayout[];
  /** Where each team forms up at the start of a match. */
  lines: Readonly<Record<Team, SpawnLine>>;
}

/** Metres between neighbouring ships in a spawn line (`LINE_SPACING`). */
const SLOT_SPACING = 220;

/** Slot 0 sits on the line's centre; the rest alternate outward. */
export function slotOffset(slot: number): number {
  if (slot === 0) return 0;
  const k = Math.ceil(slot / 2);
  return (slot % 2 === 1 ? k : -k) * SLOT_SPACING;
}

/**
 * Where seat `slot` of a line stands. The slots run across the line's
 * facing, so a line that faces +x (the classic east–west arena) spreads along
 * z and a line that faces +z spreads along x.
 */
export function spawnSlot(line: SpawnLine, slot: number): { x: number; z: number; yaw: number } {
  const off = slotOffset(slot);
  // Perpendicular to the heading (sin yaw, cos yaw) is (cos yaw, −sin yaw).
  return { x: line.x + Math.cos(line.yaw) * off, z: line.z - Math.sin(line.yaw) * off, yaw: line.yaw };
}

/** Ring size (`ZONE_RADIUS`). A literal here; the test ties them together. */
const RING = 600;

/**
 * The classic chart. Team A forms up at x = −2000 facing east and team B at
 * x = +2000 facing west (`LINE_DISTANCE` apart); the rings sit on the line
 * between them, one in the middle and one either side, so every side starts
 * within a minute's steaming of something it can hold and the middle belongs
 * to nobody until somebody sails for it. The flank rings stand 1250 m out:
 * far enough that the three do not touch (they did, at 1000, and `zoneAt`
 * gave the overlap to whichever ring came first), and still 150 m short of
 * the spawn lines' own water.
 */
export const CLASSIC_LINES: Readonly<Record<Team, SpawnLine>> = {
  A: { x: -2000, z: 0, yaw: Math.PI / 2 },
  B: { x: 2000, z: 0, yaw: -Math.PI / 2 },
};

export const CLASSIC_ZONES: readonly ZoneLayout[] = [
  { id: 'west', x: -1250, z: 0, radius: RING, near: 'A' },
  { id: 'centre', x: 0, z: 0, radius: RING, near: null },
  { id: 'east', x: 1250, z: 0, radius: RING, near: 'B' },
];

/** Open water: the classic chart with nothing on it. Seed 0 lands here. */
const OPEN: IslandLayout = { id: 'open', islands: [], zones: CLASSIC_ZONES, lines: CLASSIC_LINES };

/**
 * A scatter of five islands, none inside a ring: the rings are what the
 * battle is about, and land in one would decide it by geometry.
 */
const ARCHIPELAGO: IslandLayout = {
  id: 'archipelago',
  islands: [
    { x: -1100, z: 1250, rx: 420, rz: 260, yaw: 0.6, height: 55 },
    { x: 1200, z: -1300, rx: 460, rz: 240, yaw: -0.5, height: 60 },
    { x: 200, z: 1900, rx: 300, rz: 300, yaw: 0, height: 40 },
    { x: -300, z: -1950, rx: 340, rz: 260, yaw: 1.2, height: 45 },
    { x: 2100, z: 1500, rx: 260, rz: 200, yaw: 0.3, height: 30 },
  ],
  zones: CLASSIC_ZONES,
  lines: CLASSIC_LINES,
};

/**
 * Two long islands either side of the centre ring, leaving a channel through
 * the middle and open water round the outside: the fight is either through
 * the strait under the guns of both shores, or the long way round.
 */
const STRAIT: IslandLayout = {
  id: 'strait',
  islands: [
    { x: 0, z: 1150, rx: 1300, rz: 330, yaw: 0, height: 70 },
    { x: 0, z: -1150, rx: 1300, rz: 330, yaw: 0, height: 70 },
  ],
  zones: CLASSIC_ZONES,
  lines: CLASSIC_LINES,
};

/**
 * A ring of eight low reefs round a lagoon, with the lines pushed out to the
 * walls. Each team's near ring is on its own side of the reef, out in the
 * open; the lagoon ring in the middle is sheltered — nothing outside the reef
 * can see into it — and reached through the gaps the coast leaves between
 * the reefs, which the field decides: some are a battleship wide, some are
 * closed, so the lagoon is a place to fight *from* rather than a fortress.
 * The lines stand 5400 m apart rather than the classic 4000, which is what
 * pays for the reef between them.
 */
const ATOLL: IslandLayout = {
  id: 'atoll',
  islands: reefRing(8, 1000, 380, 170, 18),
  zones: [
    { id: 'west', x: -2000, z: 0, radius: RING, near: 'A' },
    { id: 'lagoon', x: 0, z: 0, radius: RING, near: null },
    { id: 'east', x: 2000, z: 0, radius: RING, near: 'B' },
  ],
  lines: {
    A: { x: -2700, z: 0, yaw: Math.PI / 2 },
    B: { x: 2700, z: 0, yaw: -Math.PI / 2 },
  },
};

/**
 * `count` seeds along an arc of a circle of `r` metres round `cx, cz`, from
 * angle `from` to `to` (radians, from +x toward +z), each lying along the arc.
 * Spaced closer than their length, they overlap into one bent island.
 */
function arcSeeds(cx: number, cz: number, r: number, from: number, to: number, count: number, rx: number, rz: number, height: number): Island[] {
  const seeds: Island[] = [];
  for (let i = 0; i < count; i++) {
    const theta = from + ((to - from) * i) / (count - 1);
    // The rx axis lies along the tangent: yaw = π/2 − θ (see `insideness`).
    seeds.push({ x: cx + Math.cos(theta) * r, z: cz + Math.sin(theta) * r, rx, rz, yaw: Math.PI / 2 - theta, height });
  }
  return seeds;
}

/**
 * `count` reefs evenly round a circle of `r` metres, each lying along the
 * circle (its long axis tangent), starting half a step off the top so the
 * north–south axis runs through a gap and not a rock.
 */
function reefRing(count: number, r: number, rx: number, rz: number, height: number): Island[] {
  const reefs: Island[] = [];
  for (let i = 0; i < count; i++) {
    const theta = ((i + 0.5) / count) * Math.PI * 2;
    // A heading of `theta` points radially outward; rx along the tangent.
    reefs.push({ x: Math.sin(theta) * r, z: Math.cos(theta) * r, rx, rz, yaw: theta, height });
  }
  return reefs;
}

/**
 * A channel from south to north between two continuous banks: wide bays at
 * each end where the lines form up, a throat at each end of the basin in the
 * middle, and the three rings in a row down the channel. The banks are seeds
 * that run past the arena wall, so the land is the edge of the chart and
 * only the channel is water. The lines face north and south, so this is the
 * one chart on which the classic east–west instincts are wrong.
 */
const FJORD: IslandLayout = {
  id: 'fjord',
  islands: [
    // West bank, south to north; the seeds overlap into one shore.
    { x: -2950, z: -2300, rx: 1300, rz: 1250, yaw: 0, height: 70 },
    { x: -3050, z: 0, rx: 1400, rz: 1350, yaw: 0, height: 95 },
    { x: -2950, z: 2300, rx: 1300, rz: 1250, yaw: 0, height: 70 },
    // East bank.
    { x: 2950, z: -2300, rx: 1300, rz: 1250, yaw: 0, height: 70 },
    { x: 3050, z: 0, rx: 1400, rz: 1350, yaw: 0, height: 95 },
    { x: 2950, z: 2300, rx: 1300, rz: 1250, yaw: 0, height: 70 },
    // The throats: headlands that narrow the channel south and north of the basin.
    { x: -1550, z: -1450, rx: 550, rz: 400, yaw: 0, height: 80 },
    { x: 1550, z: -1450, rx: 550, rz: 400, yaw: 0, height: 80 },
    { x: -1550, z: 1450, rx: 550, rz: 400, yaw: 0, height: 80 },
    { x: 1550, z: 1450, rx: 550, rz: 400, yaw: 0, height: 80 },
  ],
  zones: [
    { id: 'south', x: 0, z: -1250, radius: RING, near: 'A' },
    { id: 'centre', x: 0, z: 0, radius: RING, near: null },
    { id: 'north', x: 0, z: 1250, radius: RING, near: 'B' },
  ],
  lines: {
    A: { x: 0, z: -2000, yaw: 0 },
    B: { x: 0, z: 2000, yaw: Math.PI },
  },
};

/**
 * Two mountains, one on each team's left flank, over the classic rings. A
 * line that swings left keeps its own mountain between it and the enemy's
 * near ring, and meets the enemy coming the other way round the other one:
 * the chart pushes both sides anticlockwise.
 */
const TWINS: IslandLayout = {
  id: 'twins',
  islands: [
    { x: -1100, z: 1500, rx: 900, rz: 550, yaw: 0.35, height: 95 },
    { x: 1100, z: -1500, rx: 900, rz: 550, yaw: 0.35, height: 95 },
  ],
  zones: CLASSIC_ZONES,
  lines: CLASSIC_LINES,
};

/**
 * A dozen low rocks over the classic chart. None is big enough to hide a
 * battleship for long, and none blocks a course for more than a turn, but
 * between them sight lines are short and a torpedo run has cover on the way
 * in. The rocks keep off the rings and the lines; the water between the rings
 * is where they are.
 */
const SHOALS: IslandLayout = {
  id: 'shoals',
  islands: [
    { x: -1500, z: 900, rx: 200, rz: 130, yaw: 0.4, height: 16 },
    { x: -500, z: 850, rx: 180, rz: 120, yaw: -0.3, height: 14 },
    { x: 500, z: -850, rx: 180, rz: 120, yaw: 0.2, height: 14 },
    { x: 1500, z: -900, rx: 200, rz: 130, yaw: -0.5, height: 16 },
    { x: -1500, z: -950, rx: 160, rz: 110, yaw: 0.9, height: 12 },
    { x: 1500, z: 950, rx: 160, rz: 110, yaw: -0.9, height: 12 },
    { x: 0, z: 1500, rx: 220, rz: 150, yaw: 0, height: 20 },
    { x: 0, z: -1500, rx: 220, rz: 150, yaw: 0, height: 20 },
    { x: -700, z: -1500, rx: 150, rz: 100, yaw: 0.6, height: 12 },
    { x: 700, z: 1500, rx: 150, rz: 100, yaw: 0.6, height: 12 },
    { x: -2200, z: 1800, rx: 200, rz: 140, yaw: 0.2, height: 18 },
    { x: 2200, z: -1800, rx: 200, rz: 140, yaw: 0.2, height: 18 },
  ],
  zones: CLASSIC_ZONES,
  lines: CLASSIC_LINES,
};

/**
 * One long headland across the north of the chart, and the rings pushed off
 * the centreline to the south of it: team A's near ring sits in the lee of the
 * headland's western end, the middle ring is under its guns, and team B's is
 * out in open water to the south-east. The only asymmetric chart, and the
 * seed decides which team gets the shelter.
 */
const HEADLAND: IslandLayout = {
  id: 'headland',
  islands: [
    { x: -500, z: 1400, rx: 1500, rz: 550, yaw: 0.2, height: 110 },
    { x: 1700, z: 1000, rx: 220, rz: 160, yaw: 0, height: 25 },
  ],
  zones: [
    { id: 'west', x: -1100, z: -500, radius: RING, near: 'A' },
    { id: 'centre', x: 300, z: 0, radius: RING, near: null },
    { id: 'east', x: 1300, z: -1200, radius: RING, near: 'B' },
  ],
  lines: CLASSIC_LINES,
};

/**
 * A river mouth: the whole south of the chart is land, and three low
 * sandbanks lie off it where the channels come out. The rings sit south of
 * the centreline, in the water between the banks and the shore, so the fight
 * is drawn into the shallows where sight lines are short; the open sea to
 * the north is the long way round.
 */
const DELTA: IslandLayout = {
  id: 'delta',
  islands: [
    { x: -2600, z: -2950, rx: 1100, rz: 900, yaw: 0, height: 60 },
    { x: 0, z: -3050, rx: 1400, rz: 850, yaw: 0, height: 75 },
    { x: 2600, z: -2950, rx: 1100, rz: 900, yaw: 0, height: 60 },
    { x: -1200, z: -1350, rx: 450, rz: 240, yaw: 0.3, height: 14 },
    { x: 300, z: -1550, rx: 500, rz: 220, yaw: -0.2, height: 12 },
    { x: 1550, z: -1450, rx: 400, rz: 230, yaw: 0.4, height: 14 },
  ],
  zones: [
    { id: 'west', x: -1250, z: -400, radius: RING, near: 'A' },
    { id: 'centre', x: 0, z: -300, radius: RING, near: null },
    { id: 'east', x: 1250, z: -400, radius: RING, near: 'B' },
  ],
  lines: CLASSIC_LINES,
};

/**
 * One big island bent round the middle ring, its hollow open to the north:
 * the centre ring lies in the bay, under the whole crescent's high ground,
 * and the flank rings sit off its tips. A line can go straight for the
 * middle through the bay's mouth or round the outside of the horns.
 */
const CRESCENT: IslandLayout = {
  id: 'crescent',
  // Nine seeds along an arc, close enough to overlap into one bent island.
  islands: arcSeeds(0, -300, 1200, Math.PI * 1.18, Math.PI * 1.82, 9, 480, 340, 80),
  zones: [
    { id: 'west', x: -1250, z: 400, radius: RING, near: 'A' },
    { id: 'centre', x: 0, z: -200, radius: RING, near: null },
    { id: 'east', x: 1250, z: 400, radius: RING, near: 'B' },
  ],
  lines: CLASSIC_LINES,
};

/**
 * A channel running corner to corner: the lines form up in the north-west
 * and south-east bays and the rings lie down the diagonal between two banks.
 * Every heading on this chart is off the compass, and the banks are near
 * enough that a line advancing down the channel is always under one shore or
 * the other.
 */
const SOUND: IslandLayout = {
  id: 'sound',
  islands: [
    // North-east bank: four seeds along x + z = 2000, overlapping into one shore.
    { x: 100, z: 1900, rx: 560, rz: 420, yaw: Math.PI / 4, height: 85 },
    { x: 700, z: 1300, rx: 560, rz: 420, yaw: Math.PI / 4, height: 95 },
    { x: 1300, z: 700, rx: 560, rz: 420, yaw: Math.PI / 4, height: 95 },
    { x: 1900, z: 100, rx: 560, rz: 420, yaw: Math.PI / 4, height: 85 },
    // South-west bank, along x + z = -2000.
    { x: -1900, z: -100, rx: 560, rz: 420, yaw: Math.PI / 4, height: 85 },
    { x: -1300, z: -700, rx: 560, rz: 420, yaw: Math.PI / 4, height: 95 },
    { x: -700, z: -1300, rx: 560, rz: 420, yaw: Math.PI / 4, height: 95 },
    { x: -100, z: -1900, rx: 560, rz: 420, yaw: Math.PI / 4, height: 85 },
  ],
  zones: [
    { id: 'west', x: -1000, z: 1000, radius: RING, near: 'A' },
    { id: 'centre', x: 0, z: 0, radius: RING, near: null },
    { id: 'east', x: 1000, z: -1000, radius: RING, near: 'B' },
  ],
  lines: {
    A: { x: -1900, z: 1900, yaw: (3 * Math.PI) / 4 },
    B: { x: 1900, z: -1900, yaw: -Math.PI / 4 },
  },
};

export const ISLAND_LAYOUTS: readonly IslandLayout[] = [OPEN, ARCHIPELAGO, STRAIT, ATOLL, FJORD, TWINS, SHOALS, HEADLAND, DELTA, CRESCENT, SOUND];

export function layoutForSeed(seed: number): IslandLayout {
  const i = Math.abs(seed | 0) % ISLAND_LAYOUTS.length;
  return ISLAND_LAYOUTS[i]!;
}

export function layoutById(id: LayoutId): IslandLayout {
  return ISLAND_LAYOUTS.find((l) => l.id === id) ?? OPEN;
}

/**
 * How far inside the seed ellipse a point is, as a fraction: 0 on the seed's
 * edge, 1 at the centre, negative outside. `margin` grows the ellipse by that
 * many metres. About the seed, not the coast: the coast is `landHeightAt`.
 */
export function insideness(island: Island, x: number, z: number, margin = 0): number {
  const dx = x - island.x;
  const dz = z - island.z;
  const c = Math.cos(island.yaw);
  const s = Math.sin(island.yaw);
  // Into the island's own frame: `u` along its rx axis, `v` along rz.
  const u = dx * c - dz * s;
  const v = dx * s + dz * c;
  const rx = island.rx + margin;
  const rz = island.rz + margin;
  const q = Math.sqrt((u * u) / (rx * rx) + (v * v) / (rz * rz));
  return 1 - q;
}

/**
 * The island a hull of `margin` beam is aground on, or null on open water.
 * The land is the field's; the island returned is the seed nearest the point,
 * for whoever wants a name for it.
 */
export function islandAt(layout: IslandLayout, x: number, z: number, margin = 0): Island | null {
  if (layout.islands.length === 0) return null;
  if (terrainFor(layout).heightAround(x, z, margin) <= 0) return null;
  let best: Island | null = null;
  let bestK = -Infinity;
  for (const island of layout.islands) {
    const k = insideness(island, x, z);
    if (k > bestK) {
      bestK = k;
      best = island;
    }
  }
  return best;
}

/**
 * Height of the land at a point, metres above the sea. Zero on water. Shared
 * so the shell that the server says hit rock is drawn hitting rock at the
 * same height.
 */
export function landHeightAt(layout: IslandLayout, x: number, z: number): number {
  return terrainFor(layout).heightAt(x, z);
}

/** True when a point at height `y` is inside land (below the surface of an island). */
export function onLand(layout: IslandLayout, x: number, z: number, y = 0): boolean {
  if (layout.islands.length === 0) return false;
  const h = terrainFor(layout).heightAt(x, z);
  return h > 0 && y <= h;
}

/** True when the disc of `radius` round a point has any land in it: rings, ports, seats. */
export function landInDisc(layout: IslandLayout, x: number, z: number, radius: number): boolean {
  return terrainFor(layout).landInDisc(x, z, radius);
}

/** A land strike found by marching a tick's segment, and where along it. */
export interface LandHit {
  x: number;
  z: number;
  y: number;
  /** Fraction of the way from `a` to `b`, 0…1 — comparable to a hull hit's own `t` on the same segment. */
  t: number;
}

/** Metres apart the steps are, marching a segment for a land strike. */
const LAND_MARCH_STEP = 6;

/**
 * The first point along the segment from `a` to `b` that sits on land, or
 * `null` when the whole run is clear. Marched in steps of `LAND_MARCH_STEP`
 * rather than tested only at `b`: a fast shell crosses a thin ridge in one
 * tick, and sampling just the endpoint would let it land on the far side of
 * rock it never actually cleared.
 *
 * `t` is the fraction of the segment, so a caller with a hull hit found along
 * the same `a`→`b` run can compare the two `t`s directly and let whichever is
 * smaller — rock or steel — win, without either test knowing about the other.
 */
export function firstLandOnSegment(
  layout: IslandLayout,
  a: { x: number; y: number; z: number },
  b: { x: number; y: number; z: number },
): LandHit | null {
  if (layout.islands.length === 0) return null;
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dz);
  const steps = Math.max(1, Math.ceil(len / LAND_MARCH_STEP));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const x = a.x + dx * t;
    const z = a.z + dz * t;
    const y = a.y + dy * t;
    if (onLand(layout, x, z, y)) return { x, z, y, t };
  }
  return null;
}

/** Fraction of way a hull keeps after grounding. Same as the arena wall. */
export const GROUND_SPEED_KEEP = 0.25;

export interface Grounding {
  x: number;
  z: number;
  /** True when the hull was on land and has been pushed back to the shore. */
  grounded: boolean;
}

/** Metres a grounded hull is walked back per step, and how many steps at most. */
const GROUND_STEP = 6;
const GROUND_STEPS = 120;
/** Halvings of the swept segment when finding the shore. 2^8 of a tick's travel is centimetres. */
const SWEEP_HALVINGS = 8;

const grad = { x: 0, z: 0 };

/**
 * Keep a hull off the land. The hull is a point with `margin` metres of beam
 * around it.
 *
 * With `fromX, fromZ` — where the hull was a tick ago, on water — the move
 * is swept: the shore is found by halving the segment between the two, the
 * hull is put on the water side of it, and the rest of the tick's travel is
 * tried along the shore (across the slope) so a hull that meets a coast at
 * an angle slides along it instead of sticking. It never ends up past the
 * shore it hit, whatever the land looks like beyond.
 *
 * Without a start, or with one that is itself on land (a spawn on a beach,
 * a chart that changed under a hull), it is walked back down the slope of
 * the field a few metres a step until it is off, and out along the nearest
 * seed's short axis if a ridge misleads that.
 *
 * Pure, and called by the simulation after each step, so prediction and
 * server land a grounded hull in the same place.
 */
export function groundHull(layout: IslandLayout, x: number, z: number, margin: number, fromX?: number, fromZ?: number): Grounding {
  if (layout.islands.length === 0) return { x, z, grounded: false };
  const t = terrainFor(layout);
  if (t.heightAround(x, z, margin) <= 0) return { x, z, grounded: false };

  if (fromX !== undefined && fromZ !== undefined && t.heightAround(fromX, fromZ, margin) <= 0) {
    // Sweep: the last water point on the way from `from` to here.
    let lo = 0;
    let hi = 1;
    const dx = x - fromX;
    const dz = z - fromZ;
    for (let i = 0; i < SWEEP_HALVINGS; i++) {
      const m = (lo + hi) / 2;
      if (t.heightAround(fromX + dx * m, fromZ + dz * m, margin) <= 0) lo = m;
      else hi = m;
    }
    const cx = fromX + dx * lo;
    const cz = fromZ + dz * lo;
    // Slide: the travel left over, along the shore rather than into it.
    t.gradient(cx, cz, margin, grad);
    const gl = Math.hypot(grad.x, grad.z);
    if (gl > 1e-6) {
      const tx = -grad.z / gl;
      const tz = grad.x / gl;
      const rest = (dx * (1 - lo)) * tx + (dz * (1 - lo)) * tz;
      const sx = cx + tx * rest;
      const sz = cz + tz * rest;
      if (t.heightAround(sx, sz, margin) <= 0) return { x: sx, z: sz, grounded: true };
    }
    return { x: cx, z: cz, grounded: true };
  }

  let px = x;
  let pz = z;
  for (let i = 0; i < GROUND_STEPS; i++) {
    t.gradient(px, pz, margin, grad);
    let dx = -grad.x;
    let dz = -grad.z;
    let len = Math.hypot(dx, dz);
    if (len < 1e-6) {
      // Flat: walk away from the nearest seed's centre instead.
      const island = islandAt(layout, px, pz) ?? layout.islands[0]!;
      dx = px - island.x;
      dz = pz - island.z;
      len = Math.hypot(dx, dz);
      if (len < 1e-6) {
        dx = 1;
        dz = 0;
        len = 1;
      }
    }
    px += (dx / len) * GROUND_STEP;
    pz += (dz / len) * GROUND_STEP;
    if (t.heightAround(px, pz, margin) <= 0) return { x: px, z: pz, grounded: true };
  }
  // Still aground — a ridge led the descent the long way. Walk out along the
  // nearest seed's short axis, which always reaches water inside the seed's
  // grown ellipse.
  const island = islandAt(layout, px, pz) ?? layout.islands[0]!;
  const c = Math.cos(island.yaw);
  const sn = Math.sin(island.yaw);
  const short = island.rx < island.rz;
  const ax = short ? c : -sn;
  const az = short ? -sn : c;
  const side = (px - island.x) * ax + (pz - island.z) * az >= 0 ? 1 : -1;
  for (let i = 0; i < GROUND_STEPS * 2; i++) {
    px += ax * side * GROUND_STEP;
    pz += az * side * GROUND_STEP;
    if (t.heightAround(px, pz, margin) <= 0) break;
  }
  return { x: px, z: pz, grounded: true };
}

/**
 * Whether the line from `a` to `b` is blocked by land: the field is sampled
 * every `LOS_STEP` metres between them and any sample above the eye
 * (`EYE_HEIGHT` over the sea, at both ends) is rock in the way. A hull on a
 * beach is still in sight of the sea it is on; nothing sees through a hill.
 */
export function losBlocked(layout: IslandLayout, ax: number, az: number, bx: number, bz: number): boolean {
  if (layout.islands.length === 0) return false;
  const t = terrainFor(layout);
  const dx = bx - ax;
  const dz = bz - az;
  const len = Math.hypot(dx, dz);
  const n = Math.max(1, Math.ceil(len / LOS_STEP));
  for (let i = 1; i < n; i++) {
    const k = i / n;
    if (t.heightAt(ax + dx * k, az + dz * k) > EYE_HEIGHT) return true;
  }
  return false;
}

/**
 * A bearing that goes round the land. If the course from `x,z` along
 * `desired` runs into land inside `lookahead` metres, try turning off it in
 * steps to either side and take the first clear course, nearest the wanted
 * one first. Returns `desired` itself on open water. Pure; the bots use it,
 * and it never allocates.
 */
export function avoidLand(layout: IslandLayout, x: number, z: number, desired: number, lookahead: number, margin: number): number {
  if (layout.islands.length === 0) return desired;
  if (courseClear(layout, x, z, desired, lookahead, margin)) return desired;
  const steps = [0.35, -0.35, 0.7, -0.7, 1.05, -1.05, 1.4, -1.4, 1.75, -1.75];
  for (const d of steps) {
    if (courseClear(layout, x, z, desired + d, lookahead, margin)) return desired + d;
  }
  return desired + Math.PI;
}

function courseClear(layout: IslandLayout, x: number, z: number, bearing: number, lookahead: number, margin: number): boolean {
  const t = terrainFor(layout);
  const sx = Math.sin(bearing);
  const cz = Math.cos(bearing);
  // Sample the course about every grid cell: a spit one cell wide is still a spit.
  const n = Math.max(6, Math.ceil(lookahead / (TERRAIN_CELL * 1.5)));
  for (let i = 1; i <= n; i++) {
    const d = (lookahead * i) / n;
    if (t.heightAround(x + sx * d, z + cz * d, margin) > 0) return false;
  }
  return true;
}
